// Admin Admins Tab — Multi-administrator management
// Lists all admins, allows creation, password reset, deletion (root protected)
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

export default function AdminsTab({ locale }) {
  const isFr = locale === "fr";
  const [admins, setAdmins] = useState([]);
  const [currentEmail, setCurrentEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(data.items || []);
      setCurrentEmail((data.current_email || "").toLowerCase());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(isFr ? `Supprimer l'administrateur « ${a.email} » ?` : `Delete admin "${a.email}"?`)) return;
    try {
      await api.delete(`/admin/admins/${a.id}`);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="admins-tab" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">{isFr ? "Administrateurs" : "Administrators"}</h2>
          <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
            {isFr ? "Gestion des comptes back-office." : "Back-office account management."}
          </p>
        </div>
        <button className="btn btn-primary" data-testid="admin-new"
                onClick={() => setCreating(true)}>
          + {isFr ? "Nouvel administrateur" : "New administrator"}
        </button>
      </div>

      {creating && (
        <AdminCreator isFr={isFr}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }} />
      )}

      {resetting && (
        <PasswordResetter isFr={isFr} target={resetting}
          onCancel={() => setResetting(null)}
          onSaved={() => setResetting(null)} />
      )}

      {loading && <div className="font-display italic" style={{ color: "var(--muted)" }}>…</div>}

      {!loading && (
        <div data-testid="admins-list">
          <div className="deco-rule mb-4">
            <span className="font-caps text-[0.78rem]">
              {isFr ? "Comptes" : "Accounts"} <span style={{ color: "var(--muted)" }}>· {admins.length}</span>
            </span>
          </div>
          <div className="space-y-3">
            {admins.map((a) => {
              const isMe = a.email?.toLowerCase() === currentEmail;
              return (
                <div key={a.id} data-testid={`admin-row-${a.id}`} className="bg-white border p-4 flex items-center justify-between gap-4 flex-wrap"
                     style={{ borderColor: "var(--border)" }}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display text-lg">{a.name || a.email}</span>
                      {isMe && (
                        <span className="font-caps text-[0.55rem] px-2 py-0.5 border"
                              style={{ borderColor: "var(--gold)", color: "var(--gold)", letterSpacing: "0.18em" }}>
                          {isFr ? "VOUS" : "YOU"}
                        </span>
                      )}
                    </div>
                    <div className="font-body text-sm" style={{ color: "var(--muted)" }}>{a.email}</div>
                    <div className="font-caps text-[0.55rem] mt-1" style={{ color: "var(--muted)", letterSpacing: "0.15em" }}>
                      {a.created_at && `${isFr ? "CRÉÉ LE" : "CREATED"} ${new Date(a.created_at).toLocaleDateString(locale)}`}
                      {a.created_by && ` · ${isFr ? "PAR" : "BY"} ${a.created_by}`}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button className="btn btn-outline btn-xs" data-testid={`admin-reset-${a.id}`}
                            onClick={() => setResetting(a)}>
                      {isFr ? "Mot de passe" : "Password"}
                    </button>
                    <button className="btn btn-outline btn-xs" style={{ color: "var(--cinnabar)" }}
                            data-testid={`admin-delete-${a.id}`}
                            disabled={isMe}
                            onClick={() => remove(a)}>
                      {isFr ? "Supprimer" : "Delete"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminCreator({ isFr, onCancel, onSaved }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!email || !name || pwd.length < 8) {
      setErr(isFr ? "Email, nom et mot de passe (8+ caractères) requis." : "Email, name and password (8+) required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/admins", { email, name, password: pwd });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-card space-y-4" data-testid="admin-creator">
      <h3 className="font-display text-xl">{isFr ? "Nouvel administrateur" : "New administrator"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className="label-caps">{isFr ? "Nom *" : "Name *"}</span>
          <input className="input-classic mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-name" />
        </label>
        <label className="block">
          <span className="label-caps">Email *</span>
          <input type="email" className="input-classic mt-1" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
        </label>
        <label className="block">
          <span className="label-caps">{isFr ? "Mot de passe * (8+)" : "Password * (8+)"}</span>
          <input type="text" className="input-classic mt-1 font-mono" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="admin-pwd"
                 placeholder={isFr ? "Communiquez-le en personne" : "Share securely"} />
        </label>
      </div>
      {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel} data-testid="admin-create-cancel">{isFr ? "Annuler" : "Cancel"}</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="admin-create-save">
          {busy ? "…" : (isFr ? "Créer" : "Create")}
        </button>
      </div>
    </div>
  );
}

function PasswordResetter({ isFr, target, onCancel, onSaved }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async () => {
    setErr("");
    if (pwd.length < 8) { setErr(isFr ? "Mot de passe trop court (8+)." : "Password too short (8+)."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/admins/${target.id}/password`, { new_password: pwd });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="admin-card space-y-4" data-testid="admin-password-modal">
      <h3 className="font-display text-xl">
        {isFr ? "Changer le mot de passe de " : "Change password of "}<span style={{ color: "var(--cinnabar)" }}>{target.email}</span>
      </h3>
      {done ? (
        <div>
          <p className="font-body text-sm" style={{ color: "var(--moss)" }}>
            ✓ {isFr ? "Mot de passe modifié. Communiquez-le à l'administrateur en personne." : "Password changed. Share it securely."}
          </p>
          <div className="flex justify-end mt-4">
            <button className="btn btn-primary" onClick={onSaved} data-testid="admin-pwd-close">{isFr ? "Fermer" : "Close"}</button>
          </div>
        </div>
      ) : (
        <>
          <label className="block">
            <span className="label-caps">{isFr ? "Nouveau mot de passe (8+)" : "New password (8+)"}</span>
            <input type="text" className="input-classic mt-1 font-mono" value={pwd}
                   onChange={(e) => setPwd(e.target.value)} data-testid="admin-new-pwd" />
          </label>
          {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
          <div className="flex gap-3 justify-end">
            <button className="btn btn-outline" onClick={onCancel} data-testid="admin-pwd-cancel">{isFr ? "Annuler" : "Cancel"}</button>
            <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="admin-pwd-save">
              {busy ? "…" : (isFr ? "Mettre à jour" : "Update")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
